import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Trader } from "@/lib/api/reports";
import {
  CreateOtcErrorCorrectionInput,
  UpdateOtcErrorCorrectionValidation,
  OtcErrorCorrectionDto,
  useUpdateOtcErrorCorrection,
  useDeleteOtcErrorCorrection,
} from "@/lib/api/otcErrorCorrection";
import {
  OtcErrorCorrectionForm,
  OtcErrorCorrectionFormErrors,
  dtoToFormValues,
} from "./OtcErrorCorrectionForm";

/** Maps zod issues onto the per-field error map used by the form. */
function issuesToErrors(
  issues: { path: (string | number)[]; message: string }[],
): OtcErrorCorrectionFormErrors {
  const errors: OtcErrorCorrectionFormErrors = {};
  for (const issue of issues) {
    const key = issue.path[0];
    if (typeof key === "string" && !(key in errors)) {
      errors[key as keyof OtcErrorCorrectionFormErrors] = issue.message;
    }
  }
  return errors;
}

interface UpdateErrorCorrectionModalProps {
  open: boolean;
  row: OtcErrorCorrectionDto | null;
  traders: Trader[];
  onClose: () => void;
  onUpdated: () => void;
  onDeleted: () => void;
}

export function UpdateErrorCorrectionModal({
  open,
  row,
  traders,
  onClose,
  onUpdated,
  onDeleted,
}: UpdateErrorCorrectionModalProps) {
  const updateErrorCorrection = useUpdateOtcErrorCorrection();
  const deleteErrorCorrection = useDeleteOtcErrorCorrection();

  const [values, setValues] = useState<CreateOtcErrorCorrectionInput | null>(
    null,
  );
  const [errors, setErrors] = useState<OtcErrorCorrectionFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Seed from the existing row each time the modal opens.
  useEffect(() => {
    if (!open || !row) return;
    setValues(dtoToFormValues(row));
    setErrors({});
    setSubmitError(null);
    setConfirmingDelete(false);
  }, [open, row]);

  const busy = isSaving || isDeleting;

  const handleSubmit = async () => {
    if (busy || !row || !values) return;
    setSubmitError(null);

    const parsed = UpdateOtcErrorCorrectionValidation.safeParse(values);
    if (!parsed.success) {
      setErrors(issuesToErrors(parsed.error.issues));
      return;
    }
    setErrors({});

    setIsSaving(true);
    try {
      const result = await updateErrorCorrection(row.id, parsed.data);
      if (!result.success) {
        setSubmitError(result.message || "Failed to update error correction.");
        return;
      }
      onUpdated();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (busy || !row) return;
    setSubmitError(null);
    setIsDeleting(true);
    try {
      const result = await deleteErrorCorrection(row.id);
      if (!result.success) {
        setSubmitError(result.message || "Failed to delete error correction.");
        return;
      }
      onDeleted();
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (busy) return;
    if (!next) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-none w-[60vw] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Update Error Correction</DialogTitle>
        </DialogHeader>

        <div className="grow overflow-y-auto pr-1 flex flex-col gap-4">
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {row && values && (
            <div className="rounded-lg border p-4">
              <OtcErrorCorrectionForm
                values={values}
                onChange={setValues}
                traders={traders}
                errors={errors}
              />
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 flex sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            {confirmingDelete ? (
              <>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={busy}
                >
                  {isDeleting ? "Deleting..." : "Confirm Delete"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <span className="text-sm text-muted-foreground">
                  Are you sure you want to delete this error correction?
                </span>
              </>
            ) : (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setConfirmingDelete(true)}
                disabled={busy}
              >
                Delete
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={busy}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
